import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosBaseURL from "..";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";

/**
 * Bank-aware loan config CRUD client. Backed by `LoanConfigController` at
 * `/api/loan-config` on the dagri service. Every config row is scoped to a
 * specific bank (`bankId`) and loan product (`loanProductId`); the same
 * (bank, loanProductId) pair has at most one ACTIVE row.
 *
 * Note: the backend never serialises the Qena API key on responses; instead
 * it returns `qenaApiKeyConfigured: boolean`. Sending an empty/missing
 * `qenaApiKey` on update therefore leaves the existing key in place.
 */

export type LoanConfigStatus = "ACTIVE" | "INACTIVE";

export interface LoanConfig {
  id: string;
  bankId?: number | null;
  bankName?: string | null;
  loanProductId: string;
  qenaApiBaseUrl?: string | null;
  qenaApiKeyConfigured: boolean;
  qenaCallbackUrl?: string | null;
  status: LoanConfigStatus;

  loanInterest: number;
  minLoanAmount: number;
  loanCap: number;
  maxLoanAmount: number;
  minInterest: number;
  maxInterest: number;
  minRepaymentDays: number;
  maxRepaymentDays: number;
}

export interface LoanConfigPayload {
  bankId: number;
  loanProductId: string;
  qenaApiBaseUrl?: string | null;
  /** Leave undefined / empty to keep the existing stored key when editing. */
  qenaApiKey?: string | null;
  qenaCallbackUrl?: string | null;
  status?: LoanConfigStatus;

  loanInterest: number;
  minLoanAmount: number;
  loanCap: number;
  maxLoanAmount: number;
  minInterest: number;
  maxInterest: number;
  minRepaymentDays: number;
  maxRepaymentDays: number;
}

const LOAN_CONFIG_KEY = "loan-config";

export const useGetLoanConfigsForBank = (bankId?: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: [LOAN_CONFIG_KEY, "byBank", bankId],
    enabled: !!user?.token && !!bankId,
    staleTime: 60_000,
    queryFn: async (): Promise<LoanConfig[]> => {
      const { data } = await axiosBaseURL.get<LoanConfig[]>(
        `/loan-config/bank/${bankId}`,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data ?? [];
    },
  });
};

export const useGetActiveLoanConfig = (
  bankId?: number,
  loanProductId?: string,
) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: [LOAN_CONFIG_KEY, "active", bankId, loanProductId],
    enabled: !!user?.token && !!bankId && !!loanProductId,
    queryFn: async (): Promise<LoanConfig | null> => {
      const { data } = await axiosBaseURL.get<LoanConfig>(
        `/loan-config/bank/${bankId}/product/${loanProductId}`,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data ?? null;
    },
  });
};

export const useUpsertLoanConfig = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: LoanConfigPayload): Promise<LoanConfig> => {
      // Strip `qenaApiKey` when the caller didn't supply a value so the
      // backend keeps the existing one (Spring won't unset a field that
      // isn't present in the JSON body).
      const body: LoanConfigPayload = { ...payload };
      if (!body.qenaApiKey) delete (body as Partial<LoanConfigPayload>).qenaApiKey;

      const { data } = await axiosBaseURL.post<LoanConfig>(
        `/loan-config`,
        body,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [LOAN_CONFIG_KEY, "byBank", variables.bankId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          LOAN_CONFIG_KEY,
          "active",
          variables.bankId,
          variables.loanProductId,
        ],
      });
    },
  });
};
